'use client';

import { useEffect, useState, useRef } from 'react';

export default function XBackground() {
  const [isMobile, setIsMobile] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Delay video loading until user interaction or after initial render
  useEffect(() => {
    // Load video after a delay to prioritize initial page load
    const timer = setTimeout(() => {
      setShouldLoadVideo(true);
    }, 1000); // Load after 1 second

    // Also load immediately if user interacts
    const handleInteraction = () => {
      setShouldLoadVideo(true);
      clearTimeout(timer);
    };
    
    window.addEventListener('scroll', handleInteraction, { once: true, passive: true });
    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Load video source when shouldLoadVideo becomes true
  useEffect(() => {
    if (shouldLoadVideo && videoRef.current) {
      videoRef.current.load();
    }
  }, [shouldLoadVideo]);

  // Handle video loaded event for fade-in animation
  const handleVideoLoaded = () => {
    setIsVideoReady(true);
  };
  
  if (isMobile) {
    return (
      <div className="relative h-screen w-full overflow-hidden">
        {shouldLoadVideo ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            onLoadedData={handleVideoLoaded}
            className="absolute bottom-0 w-full h-1/2 object-cover z-9 transition-opacity duration-1000 ease-in-out"
            style={{
              opacity: isVideoReady ? 1 : 0,
            }}
          >
            <source src="/assets/bg-video.webm" type="video/webm" />
            <source src="/assets/bg-video.mp4" type="video/mp4" />
          </video>
        ) : (
          <div className="absolute bottom-0 w-full h-1/2 bg-gradient-to-t from-black to-transparent z-9" />
        )}

        <div 
          className="absolute inset-0 z-11"
          style={{
            background: '#000000cc'
          }}
        />
      </div>
    );
  } else {
    return (
      <div className="relative h-screen w-full overflow-hidden">
        {shouldLoadVideo ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            onLoadedData={handleVideoLoaded}
            className="absolute inset-0 w-full h-full object-cover z-9 transition-opacity duration-1000 ease-in-out"
            style={{
              opacity: isVideoReady ? 1 : 0,
            }}
          >
            <source src="/assets/bg-video.webm" type="video/webm" />
            <source src="/assets/bg-video.mp4" type="video/mp4" />
          </video>
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-black via-black to-transparent z-9" />
        )}
        <div 
          className="absolute inset-0 z-10"
          style={{
            background: '#000000cc'
          }}
        />
      </div>
    );
  }
}